import { useSelector } from 'react-redux';
import i18n from 'i18n-js';
import Operations from '@components/search/operations';
import { OperationSearchItem } from '@interfaces/search';
import React, { FC } from 'react';
import ImagesContainer, { ImagesContainerMode } from '@v2/pages/common/components/ImagesContainer/ImagesContainer';
import { getJobOrOrderStatusConfig, SCREEN_BORDER_RADIUS, SCREEN_PADDING_VERTICAL_FOR_ITEM } from '@v2/pages/common/constants/base.constants';
import StatusBadge from '@components/common/StatusBadge';
import { Text, View } from 'react-native';
import { injectConditionalArrayItems, isPhone, isWeb } from '@helpers';
import { getBoxShadowStyle } from '@styles/shadow';
import ColorWrapper, { WithColor } from '@styles/colors';
import { scale as initialScale } from '@components/frame';
import InputLine from '@components/machines/components/detailsComponents/activeMachine/components/InputLine';
import { ItemName, Period } from '@v2/pages/batch/details/modules/batch/components/BatchDetailsBlock/BatchDetailsBlockContent';
import { font as initialFont } from '@styles/fonts';
import Loading from '@components/loading';
import { DOCUMENT_ENTITY_TYPE } from '@components/imageHandling/common';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import TimeDisplayInStaticFormat from '@components/common/TimeDisplayInStaticFormat';
import { useManageJobContent } from '@v2/pages/machines/modules/now/components/Job/hooks/useManageJobContent';
import { BlockTitle, SelectMachineForJobLine } from './components';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

// Re-export sub-components for backward compatibility
export { BlockTitle, SelectMachineForJobLine } from './components';

export interface JobContentProps extends WithColor {
  withImages?: boolean;
  maxDetailsWidth: number;
  shouldTakeFromBatch?: boolean;
  currentJobId?: string | number | null;
  jobDataLoadingForList?: boolean;
  bg?: string;
  displaySingleJob?: boolean;
  ignoreHeaderConfig?: boolean;
  jobs: OperationSearchItem[];
  loading?: boolean;
  scale?: ScaleFunction;
  font?: typeof initialFont;
}

const JobContentComponent: FC<JobContentProps> = ({
  withImages,
  maxDetailsWidth,
  shouldTakeFromBatch,
  C,
  jobs,
  loading,
  currentJobId,
  jobDataLoadingForList,
  displaySingleJob,
  scale = initialScale,
  font = initialFont,
  ignoreHeaderConfig = false,
  bg = 'Background/Light/Field',
}) => {
  const {
    batch,
    generateImageURL,
    safeIsNowSubScreenCheck,
    showList,
    multipleJobsInHeader,
    jobIds,
    machineByJobStatus,
    isError,
    containerWidth,
    setContainerWidth,
    shouldNotShowLoader,
    fetchJobsData,
    toggleMachines,
    currentJob,
    onJobNumberClick,
    onProductionOrderNumberClick,
    onJobImageClick,
    shouldDisplayJobNumberAsText,
    machineName,
    borderRadiusConfig,
    showMarkAsFinished,
    handleMarkAsFinished,
  } = useManageJobContent({ jobs, shouldTakeFromBatch, currentJobId, jobDataLoadingForList, ignoreHeaderConfig, scale });

  if (!jobIds.length && !loading) return null;

  const topBlock = [
    {
      testID: TEST_IDS.jobContent.field('job'),
      borderBottomRadius: false,
      borderTopRadius: !withImages,
      onlyLabel: false,
      data: [],
      index: 0,
      i: {
        name: 'Job',
        isText: shouldDisplayJobNumberAsText,
        ...(shouldDisplayJobNumberAsText
          ? { value: currentJob?.productionOperationNumber }
          : {
              ValueComponent: <ItemName {...{ C, onPress: onJobNumberClick, text: currentJob?.productionOperationNumber, scale, font }} />,
            }),
      },
      C,
      loading,
    },
    {
      testID: TEST_IDS.jobContent.field('productionOrder'),
      borderBottomRadius: false,
      borderTopRadius: false,
      onlyLabel: false,
      data: [],
      index: 0,
      i: {
        name: 'Production Order',
        isText: showList,
        ...(showList
          ? { value: currentJob?.productionOrderNumber }
          : {
              ValueComponent: <ItemName {...{ C, onPress: onProductionOrderNumberClick, text: currentJob?.productionOrderNumber, scale, font, testID: TEST_IDS.jobContent.productionOrderLink }} />,
            }),
      },
      C,
      loading,
    },
    {
      testID: TEST_IDS.jobContent.field('operation'),
      borderBottomRadius: false,
      borderTopRadius: false,
      onlyLabel: false,
      data: [],
      index: 0,
      i: {
        name: 'Operation',
        value: currentJob?.operationName,
      },
      C,
      loading,
    },
    ...(currentJob?.outsourced !== true
      ? [
          {
            testID: TEST_IDS.jobContent.field('machine'),
            borderBottomRadius: false,
            borderTopRadius: false,
            onlyLabel: false,
            data: [],
            index: 0,
            i: {
              name: machineByJobStatus?.label ?? 'Machine',
              ...(machineName ? { value: machineName } : { ValueComponent: <SelectMachineForJobLine C={C} scale={scale} font={font} /> }),
              action: toggleMachines,
            },
            C,
            loading,
          },
        ]
      : []),
    {
      testID: TEST_IDS.jobContent.field('status'),
      borderBottomRadius: !showMarkAsFinished,
      borderTopRadius: false,
      onlyLabel: false,
      data: showMarkAsFinished ? [] : [{}],
      index: 0,
      i: {
        name: 'Status',
        ...(currentJob?.status
          ? { ValueComponent: <StatusBadge C={C} config={getJobOrOrderStatusConfig(currentJob.status, C)} scale={scale} font={font} /> }
          : { isText: true, value: 'Not Active' }),
      },
      C,
      loading,
    },
    ...injectConditionalArrayItems(showMarkAsFinished, {
      testID: TEST_IDS.jobContent.field('markAsFinished'),
      borderBottomRadius: true,
      borderTopRadius: false,
      onlyLabel: true,
      data: [{}],
      index: 0,
      i: {
        name: 'markAsFinished',
        action: handleMarkAsFinished,
        Component: () => (
          <Text numberOfLines={1} style={[{ color: C('Link/Light/Primary') }, font.Body]}>
            {i18n.t('Mark As Finished...')}
          </Text>
        ),
      },
      C,
      loading,
    }),
  ];

  const scheduleBlockData = [
    ...(currentJob?.actualStartDate
      ? [
          {
            testID: TEST_IDS.jobContent.field('actualStart'),
            borderBottomRadius: false,
            borderTopRadius: true,
            onlyLabel: false,
            data: [],
            index: 0,
            i: {
              name: 'Actual Start',
              ValueComponent: <Period startedOn={currentJob?.actualStartDate} C={C} font={font} />,
            },
            C,
            loading,
          },
        ]
      : []),
    ...(currentJob?.actualEndDate
      ? [
          {
            testID: TEST_IDS.jobContent.field('actualEnd'),
            borderBottomRadius: false,
            borderTopRadius: !currentJob?.actualStartDate,
            onlyLabel: false,
            data: [],
            index: 0,
            i: {
              name: 'Actual End',
              ValueComponent: <Period startedOn={currentJob?.actualEndDate} C={C} font={font} />,
            },
            C,
            loading,
          },
        ]
      : []),
    {
      testID: TEST_IDS.jobContent.field('scheduledStart'),
      borderBottomRadius: false,
      borderTopRadius: !currentJob?.actualStartDate && !currentJob?.actualEndDate,
      onlyLabel: false,
      data: [],
      index: 0,
      i: {
        name: 'Scheduled Start',
        ValueComponent: <Period startedOn={currentJob?.scheduledStartDate} C={C} font={font} />,
      },
      C,
      loading,
    },
    {
      testID: TEST_IDS.jobContent.field('scheduledEnd'),
      borderBottomRadius: true,
      borderTopRadius: false,
      onlyLabel: false,
      data: [{}],
      index: 0,
      i: {
        name: 'Scheduled End',
        ValueComponent: <Period startedOn={currentJob?.scheduledEndDate} C={C} font={font} />,
      },
      C,
      loading,
    },
  ];

  const timesBlockData = [
    {
      testID: TEST_IDS.jobContent.field('total'),
      borderBottomRadius: false,
      borderTopRadius: true,
      onlyLabel: false,
      data: [],
      index: 0,
      i: {
        name: 'Total',
        ValueComponent: <TimeDisplayInStaticFormat C={C} timeValue={currentJob?.totalTimeScheduledSeconds ?? 0} scale={scale} font={font} />,
      },
      C,
      loading,
    },
    {
      testID: TEST_IDS.jobContent.field('setup'),
      borderBottomRadius: false,
      borderTopRadius: false,
      onlyLabel: false,
      data: [],
      index: 0,
      i: {
        name: 'Setup',
        ValueComponent: <TimeDisplayInStaticFormat C={C} timeValue={currentJob?.setupTimeSeconds ?? 0} scale={scale} font={font} />,
      },
      C,
      loading,
    },
    {
      testID: TEST_IDS.jobContent.field('perPiece'),
      borderBottomRadius: true,
      borderTopRadius: false,
      onlyLabel: false,
      data: [{}],
      index: 0,
      i: {
        name: 'Per Piece',
        ValueComponent: <TimeDisplayInStaticFormat C={C} timeValue={currentJob?.operationTimeSeconds ?? 0} scale={scale} font={font} />,
      },
      C,
      loading,
    },
  ];

  const partBlockData = [
    {
      testID: TEST_IDS.jobContent.field('partDescription'),
      borderBottomRadius: false,
      borderTopRadius: true,
      onlyLabel: false,
      data: [],
      index: 0,
      i: {
        name: 'Part Description',
        value: currentJob?.partDescription,
        isText: true,
      },
      C,
      loading,
    },
    {
      testID: TEST_IDS.jobContent.field('drawing'),
      borderBottomRadius: true,
      borderTopRadius: false,
      onlyLabel: false,
      data: [{}],
      index: 0,
      i: {
        name: 'Drawing',
        value: `${currentJob?.partDrawingNumber}${currentJob?.partDrawingPosition ? '-' + currentJob?.partDrawingPosition : ''}${
          currentJob?.partDrawingRevision ? '-' + currentJob?.partDrawingRevision : ''
        }`,
        isText: true,
      },
      C,
      loading,
    },
  ];

  const erpInfoBlockData = [
    {
      testID: TEST_IDS.jobContent.field('modifiedOn'),
      borderBottomRadius: false,
      borderTopRadius: true,
      onlyLabel: false,
      data: [],
      index: 0,
      i: {
        name: 'Modified On',
        ValueComponent: <Period C={C} startedOn={currentJob?.modifiedOn} font={font} />,
      },
      C,
      loading,
    },
    {
      testID: TEST_IDS.jobContent.field('modifiedBy'),
      borderBottomRadius: false,
      borderTopRadius: false,
      onlyLabel: false,
      data: [],
      index: 0,
      i: {
        name: 'Modified By',
        value: currentJob?.modifiedBy,
        isText: true,
      },
      C,
      loading,
    },
    {
      testID: TEST_IDS.jobContent.field('createdOn'),
      borderBottomRadius: false,
      borderTopRadius: false,
      onlyLabel: false,
      data: [],
      index: 0,
      i: {
        name: 'Created On',
        ValueComponent: <Period C={C} startedOn={currentJob?.createdOn} font={font} />,
      },
      C,
      loading,
    },
    {
      testID: TEST_IDS.jobContent.field('createdBy'),
      borderBottomRadius: true,
      borderTopRadius: false,
      onlyLabel: false,
      data: [{}],
      index: 0,
      i: {
        name: 'Created By',
        value: currentJob?.createdBy,
        isText: true,
      },
      C,
      loading,
    },
  ];

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  const styles = [
    isWeb && shadowStyles,
    borderRadiusConfig,
    { backgroundColor: C(bg), marginBottom: scale(isPhone ? 15 : 18) },
    safeIsNowSubScreenCheck && { paddingHorizontal: scale(2) },
  ];

  return (
    <View
      onLayout={(event) => {
        const { width } = event.nativeEvent.layout;
        setContainerWidth(width);
      }}
      style={{ flex: 1, width: '100%' }}
    >
      {(loading || !shouldNotShowLoader) && (
        <View testID={TEST_IDS.jobContent.loading} style={{ marginTop: scale(40) }}>
          <Loading background="transparent" />
        </View>
      )}
      {!displaySingleJob && jobs.length > 1 && shouldNotShowLoader && (
        <Operations
          {...{
            totalSearchResult: jobs.length,
            searchResult: jobs,
            isError,
            loaded: !loading,
            C,
            isSmallMode: false,
            isLineMode: false,
            fetchData: fetchJobsData,
            generateImageURL,
            recentlyViewed: [],
            setRecentlyViewed: () => {},
            isTableMode: false,
            withoutHorizontalPaddings: true,
            width: containerWidth,
            scale,
            font,
          }}
        />
      )}

      {!multipleJobsInHeader && jobs.length === 1 && shouldNotShowLoader && (
        <>
          <View
            testID={TEST_IDS.jobContent.topBlock}
            style={[
              styles,
              safeIsNowSubScreenCheck && { paddingTop: scale(SCREEN_PADDING_VERTICAL_FOR_ITEM) },
              isWeb && { borderRadius: scale(SCREEN_BORDER_RADIUS) },
              isWeb && shadowStyles,
            ]}
          >
            {withImages && jobs.length === 1 && (
              <ImagesContainer
                {...{
                  maxDetailsWidth,
                  panelViewMode: false,
                  mode: ImagesContainerMode.ROW,
                  documentRetrieveKey: DOCUMENT_ENTITY_TYPE.ProductionOperation,
                  documentRetrieveId: jobs?.[0]?.productionOperationId,
                  onImagePress: onJobImageClick,
                  scale,
                  font,
                }}
              />
            )}

            {topBlock.map((d) => (
              <React.Fragment key={d.i.name}>
                <InputLine {...{ ...d, bg, scale, font }} />
              </React.Fragment>
            ))}
          </View>

          <BlockTitle C={C} testID={TEST_IDS.jobContent.blockTitle('SCHEDULE')} title="SCHEDULE" scale={scale} font={font} />
          <View style={[styles]}>
            {scheduleBlockData.map((d) => (
              <React.Fragment key={d.i.name}>
                <InputLine {...{ ...d, bg, scale, font }} />
              </React.Fragment>
            ))}
          </View>

          <BlockTitle C={C} testID={TEST_IDS.jobContent.blockTitle('TIMES')} title="TIMES" scale={scale} font={font} />
          <View style={styles}>
            {timesBlockData.map((d) => (
              <React.Fragment key={d.i.name}>
                <InputLine {...{ ...d, bg, scale, font }} />
              </React.Fragment>
            ))}
          </View>

          <View style={styles}>
            {partBlockData.map((d) => (
              <React.Fragment key={d.i.name}>
                <InputLine {...{ ...d, bg, scale, font }} />
              </React.Fragment>
            ))}
          </View>

          <BlockTitle C={C} testID={TEST_IDS.jobContent.blockTitle('ERP INFORMATION')} title="ERP INFORMATION" scale={scale} font={font} />
          <View style={styles}>
            {erpInfoBlockData.map((d) => (
              <React.Fragment key={d.i.name}>
                <InputLine {...{ ...d, bg, scale, font }} />
              </React.Fragment>
            ))}
          </View>
        </>
      )}
    </View>
  );
};

const JobContent = ColorWrapper(JobContentComponent);

export { JobContent };
